import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const TicketsPagination = ({
  currentPage = 1,
  totalPages = 1,
  totalRecords = 0,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  entityName = 'tickets'
}) => {
  const [jumpPage, setJumpPage] = useState('');

  if (totalRecords === 0) return null;

  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(totalRecords, currentPage * pageSize);

  // Algoritmo de elipsis inteligente:
  // NUNCA genera más de 7 elementos en el DOM, sin importar si hay 10, 1,000 o 50,000 páginas.
  const getPageItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Cerca del inicio: 1, 2, 3, 4, 5, ..., totalPages
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }

    // Cerca del final: 1, ..., totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages
    if (currentPage >= totalPages - 3) {
      return [
        1,
        '...',
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      ];
    }

    // En medio: 1, ..., currentPage-1, currentPage, currentPage+1, ..., totalPages
    return [
      1,
      '...',
      currentPage - 1,
      currentPage,
      currentPage + 1,
      '...',
      totalPages
    ];
  };

  const pageItems = getPageItems();

  const handleJumpSubmit = e => {
    e.preventDefault();
    const target = parseInt(jumpPage, 10);
    if (!isNaN(target) && target >= 1 && target <= totalPages && target !== currentPage) {
      onPageChange && onPageChange(target);
      setJumpPage('');
    }
  };

  return (
    <div className="bg-white border rounded-3 px-3 py-2 mt-3 d-flex justify-content-between align-items-center flex-wrap gap-2 shadow-none">
      {/* Resumen con formato de miles */}
      <div className="text-700 fs--1">
        Mostrando <span className="fw-semi-bold text-900">{startRecord.toLocaleString()}</span> -{' '}
        <span className="fw-semi-bold text-900">{endRecord.toLocaleString()}</span> de{' '}
        <span className="fw-semi-bold text-900">{totalRecords.toLocaleString()}</span> {entityName}
      </div>

      <div className="d-flex align-items-center gap-2 flex-wrap">
        {/* Selector de registros por página */}
        {onPageSizeChange && (
          <div className="d-flex align-items-center gap-2">
            <span className="text-600 fs--1 text-nowrap">Filas:</span>
            <Form.Select
              size="sm"
              className="shadow-none border-300 py-1"
              style={{ width: '82px', fontSize: '0.80rem', cursor: 'pointer' }}
              value={pageSize}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              disabled={isLoading}
            >
              {[10, 20, 50, 100].map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Form.Select>
          </div>
        )}

        {/* Paginador con elipsis inteligente */}
        {totalPages > 1 && (
          <nav className="d-flex align-items-center gap-1">
            {/* Botón Anterior */}
            <Button
              variant="falcon-default"
              size="sm"
              className="border-0 px-2 py-1 text-secondary"
              style={{
                minWidth: '32px',
                height: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => onPageChange && onPageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              title="Página anterior"
            >
              <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.75rem' }} />
            </Button>

            {/* Números y elipsis */}
            {pageItems.map((item, index) => {
              if (item === '...') {
                const isPrevEllipsis = index === 1;
                const jumpStep = isPrevEllipsis
                  ? Math.max(1, currentPage - 5)
                  : Math.min(totalPages, currentPage + 5);

                return (
                  <Button
                    key={`ellipsis-${index}`}
                    variant="falcon-default"
                    size="sm"
                    className="border-0 px-1 py-1 text-secondary"
                    style={{
                      minWidth: '32px',
                      height: '32px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.82rem'
                    }}
                    onClick={() => onPageChange && onPageChange(jumpStep)}
                    title={isPrevEllipsis ? 'Retroceder 5 páginas' : 'Avanzar 5 páginas'}
                    disabled={isLoading}
                  >
                    &hellip;
                  </Button>
                );
              }

              const isActive = item === currentPage;
              return (
                <Button
                  key={item}
                  variant={isActive ? 'primary' : 'falcon-default'}
                  size="sm"
                  className={`px-2 py-1 fw-bold ${isActive ? 'shadow-sm text-white' : 'text-700'}`}
                  style={{
                    minWidth: '32px',
                    height: '32px',
                    fontSize: '0.82rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => onPageChange && onPageChange(item)}
                  disabled={isLoading || isActive}
                >
                  {item}
                </Button>
              );
            })}

            {/* Botón Siguiente */}
            <Button
              variant="falcon-default"
              size="sm"
              className="border-0 px-2 py-1 text-secondary"
              style={{
                minWidth: '32px',
                height: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => onPageChange && onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              title="Página siguiente"
            >
              <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.75rem' }} />
            </Button>
          </nav>
        )}

        {/* Salto rápido a página cuando hay muchas páginas (ej. más de 7 páginas) */}
        {totalPages > 7 && (
          <form onSubmit={handleJumpSubmit} className="d-flex align-items-center gap-1 ms-1">
            <span className="text-600 fs--1 text-nowrap d-none d-sm-inline">Ir a:</span>
            <Form.Control
              type="number"
              size="sm"
              min={1}
              max={totalPages}
              placeholder="#"
              value={jumpPage}
              onChange={e => setJumpPage(e.target.value)}
              className="shadow-none border-300 py-1 px-1 text-center"
              style={{ width: '48px', fontSize: '0.80rem' }}
              title={`Ingresa un número entre 1 y ${totalPages} y presiona Enter`}
              disabled={isLoading}
            />
          </form>
        )}
      </div>
    </div>
  );
};

export default TicketsPagination;